import { Stack, Tab, TabList, TabPanel, Tabs, Typography } from "@mui/joy";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import ReceiptOutlined from "@mui/icons-material/ReceiptOutlined";
import { useState } from "react";
import Messages from "./Messages/Messages";
import Files from "./Files/Files";
import AlertsChecker from "./AlertsChecker";
import ReservationLines from "./ReservationLines";
import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { GetInvoiceDetailsResponseObj } from "@/types/interfaces/crm";
import { getAdminTripMessages } from "@/services/api/crm/messages";
import { getAdminTripFiles } from "@/services/api/crm/files";
import Ledger from "./Ledger";

interface Props {
  tripId: number;
  invoiceDetailsQueryClient: UseQueryResult;
  alertsCheckerExpanded: boolean;
  setAlertsCheckerExpanded: (expanded: boolean) => void;
  alertsCheckerLoading: boolean;
  alertsCheckerError: boolean;
  checkResults: any[];
}

const filesMessagesTabs = {
  invoiceLines: {
    id: "invoiceLines",
    accessName: (queryClient: any) =>
      `Invoice Lines (${
        (queryClient?.data as unknown as GetInvoiceDetailsResponseObj)?.data?.reservations.length || 0
      })`,
    name: "Invoice Lines",
    Icon: DescriptionOutlinedIcon,
  },
  messages: {
    id: "messages",
    accessName: (queryClient: any) => `Messages (${queryClient?.data?.messages?.length || 0})`,
    Icon: MarkEmailReadOutlinedIcon,
  },
  files: {
    id: "files",
    accessName: (queryClient: any) => `Files (${queryClient?.data?.files?.length || 0})`,
    Icon: FolderOpenOutlinedIcon,
  },
  ledger: {
    id: "ledger",
    name: "Ledger",
    Icon: ReceiptOutlined,
  },
};
export default function FinanceQaTabs({
  tripId,
  invoiceDetailsQueryClient,
  alertsCheckerExpanded,
  setAlertsCheckerExpanded,
  alertsCheckerLoading,
  alertsCheckerError,
  checkResults,
}: Props) {
  const [activeTab, setActiveTab] = useState(filesMessagesTabs.invoiceLines.id);

  const messagesQueryClient = useQuery({
    queryKey: ["interfaceValidatorMessages", tripId],
    queryFn: () => getAdminTripMessages(tripId),
  });

  const filesQueryClient = useQuery({
    queryKey: ["interfaceValidatorFiles", tripId],
    queryFn: () => getAdminTripFiles(tripId),
  });
  return (
    <>
      <Tabs
        orientation="horizontal"
        color="primary"
        size="sm"
        value={activeTab}
        onChange={(_, val) => setActiveTab(val as string)}
        sx={{
          flex: "1 1 0%",
          bgcolor: "white",
          minHeight: "400px",
          width: "100%",
        }}
      >
        <TabList>
          {Object.keys(filesMessagesTabs).map((tabId) => (
            <Tab
              key={(filesMessagesTabs as any)[tabId].id}
              value={(filesMessagesTabs as any)[tabId].id}
              color={activeTab === (filesMessagesTabs as any)[tabId].id ? "primary" : undefined}
              sx={{
                fontWeight: activeTab === (filesMessagesTabs as any)[tabId].id ? "600" : "md",
                py: 0.8,
                borderRadius: "8px 8px 0 0",
                px: 2,
                "&.Mui-selected": {
                  backgroundColor: "#e7f2ff !important",
                },
              }}
            >
              <Typography
                fontSize={"sm"}
                color={activeTab === (filesMessagesTabs as any)[tabId].id ? "primary" : undefined}
                gap={0.5}
                startDecorator={(() => {
                  const Icon = (filesMessagesTabs as any)[tabId].Icon;
                  return <Icon />;
                })()}
              >
                {(filesMessagesTabs as any)[tabId].accessName
                  ? (filesMessagesTabs as any)[tabId].accessName(
                      (filesMessagesTabs as any)[tabId].id === "invoiceLines"
                        ? invoiceDetailsQueryClient
                        : (filesMessagesTabs as any)[tabId].id === "messages"
                        ? messagesQueryClient
                        : filesQueryClient,
                    )
                  : (filesMessagesTabs as any)[tabId].name}
              </Typography>
            </Tab>
          ))}
        </TabList>
        <TabPanel value={filesMessagesTabs.invoiceLines.id} sx={{ py: 3 }}>
          <Stack gap={3}>
            <AlertsChecker
              expandedRow={alertsCheckerExpanded}
              isLoading={alertsCheckerLoading}
              isError={alertsCheckerError}
              checkResults={checkResults}
              setExpandedRow={setAlertsCheckerExpanded}
            />
            <ReservationLines checkResults={checkResults} invoiceDetailsQueryClient={invoiceDetailsQueryClient} />
          </Stack>
        </TabPanel>
        <TabPanel value={filesMessagesTabs.messages.id} sx={{ py: 3 }}>
          <Messages queryClient={messagesQueryClient} />
        </TabPanel>
        <TabPanel value={filesMessagesTabs.files.id} sx={{ py: 3 }}>
          <Files queryClient={filesQueryClient} />
        </TabPanel>
        <TabPanel value={filesMessagesTabs.ledger.id} sx={{ py: 3 }}>
           <Ledger invoiceDetailsQueryClient={invoiceDetailsQueryClient} />
        </TabPanel>
      </Tabs>
    </>
  );
}
